import * as React from "react";
import { Box, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  platformCardSx,
  platformFonts,
  platformShadow,
  usePlatformColors,
} from "../tickitPlatformTheme";
import PlatformBadge from "./PlatformBadge";
import PlatformButton from "./PlatformButton";
import PlatformProgress from "./PlatformProgress";

function eventStatus(event) {
  if (!event?.date) return { label: "Draft", variant: "blue" };
  if (moment().isAfter(moment(event.date))) return { label: "Past", variant: "gray" };
  const days = moment(event.date).diff(moment(), "days");
  if (days <= 0) return { label: "Today", variant: "amber" };
  if (days <= 14) return { label: "Pending", variant: "amber" };
  return { label: "Confirmed", variant: "green" };
}

export default function PlatformEventCard({ event, role }) {
  const navigate = useNavigate();
  const colors = usePlatformColors();
  const status = eventStatus(event);
  const capacity = event?.capacity || event?.maxCapacity;
  const sold = event?.ticketsSold ?? event?.sold ?? 0;
  const pct = capacity ? Math.min(100, Math.round((sold / capacity) * 100)) : 0;
  const bannerGradient = event?.corporate
    ? "linear-gradient(135deg, #1a0a30 0%, #46109B 100%)"
    : "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)";
  const progressColor =
    status.variant === "amber" ? colors.accent : colors.brand;

  const handleManage = (e) => {
    e.stopPropagation();
    if (role === "Admin" || role === "Super" || role === "Partner") {
      navigate(`/event/dashboard/${event._id}`);
    } else {
      navigate(`/event/${event._id}`);
    }
  };

  return (
    <Box
      onClick={handleManage}
      sx={{
        ...platformCardSx,
        p: 0,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s ease",
        mb: 0,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: platformShadow.cardHover,
          borderColor: "var(--platform-brand)",
        },
      }}
    >
      <Box
        sx={{
          height: 90,
          background: event?.image
            ? `url(${event.image}) center/cover no-repeat`
            : bannerGradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!event?.image && (
          <Typography sx={{ fontSize: 28 }}>{event?.corporate ? "🎉" : "🎤"}</Typography>
        )}
      </Box>
      <Box sx={{ p: "14px" }}>
        <Typography
          sx={{
            fontFamily: platformFonts.display,
            fontWeight: 700,
            fontSize: "14px",
            color: "var(--platform-text)",
            mb: 0.5,
          }}
        >
          {event?.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
          <CalendarMonthOutlinedIcon sx={{ fontSize: 14, color: "var(--platform-text-3)" }} />
          <Typography sx={{ fontSize: "12px", color: "var(--platform-text-3)", fontFamily: platformFonts.body }}>
            {event?.date ? moment(event.date).format("D MMM YYYY · h:mm A") : "Date TBD"}
          </Typography>
        </Box>
        {event?.location && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.25 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "var(--platform-text-3)" }} />
            <Typography
              sx={{
                fontSize: "12px",
                color: "var(--platform-text-3)",
                fontFamily: platformFonts.body,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {event.location}
            </Typography>
          </Box>
        )}
        {capacity != null ? (
          <Box sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: "12px", color: "var(--platform-text-3)", mb: 0.5, fontFamily: platformFonts.body }}>
              Capacity: {sold} / {capacity}
            </Typography>
            <PlatformProgress value={pct} color={progressColor} />
          </Box>
        ) : (
          <Typography sx={{ fontSize: "12px", color: "var(--platform-text-3)", mb: 1.25, fontFamily: platformFonts.body }}>
            Capacity: — / —
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.25 }}>
          <PlatformBadge variant={status.variant}>{status.label}</PlatformBadge>
          <PlatformButton variant={status.label === "Draft" ? "ghost" : "primary"} size="small" onClick={handleManage}>
            {status.label === "Draft" ? "Edit" : "Manage"}
          </PlatformButton>
        </Box>
      </Box>
    </Box>
  );
}
